import type { DemandAnalysis, DemandStop, ScheduleImportRow } from "../../api/types";
import { DIRS, type Dir, hhmmToMin } from "../demand/demandMath.ts";
import {
  type LeftBehindEvent,
  type SimCall,
  type SimDemandBucket,
  type SimInput,
  type SimResult,
  type SimTotals,
  type SimTrip,
  type StopDirResult,
  type TripCallResult,
  type TripResult,
  stopDirIndex,
} from "./types.ts";

/**
 * A day on the route, minute by minute, with a timetable against the measured
 * ridership.
 *
 * Riders arrive at their stop at the rate the fare system saw them (each
 * fifteen-minute slice spread evenly), wait in the order they came, and board
 * the next bus going their way that has room. Whoever does not fit waits for
 * the one after. A bus carries its riders along its scheduled calls and lets
 * each off at their destination. That is the whole model: the schedule is
 * kept exactly, and riders are the only thing that moves differently from the
 * plan. It makes the simulation a judgement of the timetable alone, and it
 * makes it deterministic, so two timetables can be compared without noise.
 *
 * Riders are continuous quantities, not individuals: a slice of 37.5 riders a
 * day is 2.5 a minute, and 0.4 of a rider can be left behind. Figures are
 * averages over the days the ridership was averaged over, and read that way.
 *
 * Capacity is honoured: a trip is never fuller than the bus holds, and riders
 * who do not fit show up as waits, as "left behind" and, if no bus they can
 * board comes within their patience, as "unserved". That is deliberate. A
 * demand chart can say a trip is 500% full; the simulation says what that
 * means at the stop.
 */

const EPS = 1e-9;

/** Riders left behind under this are rounding, not an event. */
const EVENT_MIN_RIDERS = 0.5;

/** A wait beyond this is reported as "over ten minutes". */
const LONG_WAIT_MIN = 10;

/** Trip-links at this share of capacity count as crowded. */
const CROWDED_LOAD = 0.85;

/** A trip whose fullest point is under this share of capacity ran empty. */
const EMPTY_LOAD = 0.1;

// ── building the input ─────────────────────────────────────────────────────

/**
 * The stops and ridership of a demand analysis as the simulation wants them.
 * The analysis must have been requested at the boarding clock
 * (time_basis=boarding): a slice then says when riders reached the stop,
 * which is what a queue is built from.
 */
export function demandFromAnalysis(analysis: DemandAnalysis): Pick<SimInput, "stops" | "bucketMin" | "demand"> {
  return {
    stops: analysis.stops.map((s) => ({ index: s.index, stopId: s.stop_id, name: s.name, code: s.code })),
    bucketMin: analysis.bucket_minutes,
    demand: analysis.buckets.map((b) => ({ startMin: b.start_min, od: b.od })),
  };
}

/**
 * Timetable rows as trips with a call at every stop. A trip calls at each
 * stop of its direction at the stop template's offset from its departure;
 * when the row carries its own duration the offsets are stretched or
 * squeezed so the trip ends when the row says it does. Rows that are not
 * revenue trips (errored, not operational) are not capacity and are left out.
 */
export function tripsFromRows(rows: ScheduleImportRow[], stops: DemandStop[]): SimTrip[] {
  const n = stops.length;
  if (n < 2) return [];
  const upMax = Math.max(...stops.map((s) => s.offset_up_sec));
  const downMax = Math.max(...stops.map((s) => s.offset_down_sec));

  const trips: Array<SimTrip & { order: number }> = [];
  rows.forEach((row, i) => {
    if (row.error) return;
    const state = (row.operational_state ?? "").toLowerCase();
    if (state && state !== "operational") return;
    const start = hhmmToMin(row.start_time);
    if (start == null) return;
    const dir = (row.direction ?? "").toLowerCase();
    if (dir !== "upward" && dir !== "downward") return;

    const maxOffset = dir === "upward" ? upMax : downMax;
    const duration = row.trip_duration_min ?? 0;
    const scale = duration > 0 && maxOffset > 0 ? (duration * 60) / maxOffset : 1;

    const calls: SimCall[] = [];
    if (dir === "upward") {
      for (let s = 0; s < n; s++) calls.push({ stop: s, timeMin: start + (stops[s].offset_up_sec * scale) / 60 });
    } else {
      for (let s = n - 1; s >= 0; s--) calls.push({ stop: s, timeMin: start + (stops[s].offset_down_sec * scale) / 60 });
    }
    trips.push({
      id: row.id > 0 ? String(row.id) : `row-${row.row_no ?? i}`,
      block: row.shift_number ?? 0,
      dir,
      calls,
      order: i,
    });
  });

  // Departure order; rows that leave together keep the timetable's order.
  trips.sort((a, b) => a.calls[0].timeMin - b.calls[0].timeMin || a.order - b.order);
  return trips.map(({ order: _order, ...trip }) => trip);
}

// ── the simulation ─────────────────────────────────────────────────────────

/** Riders who reached one stop, going one way, and have not boarded yet. */
interface Parcel {
  /** They arrived evenly spread over [from, to). */
  from: number;
  to: number;
  riders: number;
  /** A full bus has already passed them by. */
  leftOnce: boolean;
}

/** A stop in one direction of travel: the queue and its bookkeeping. */
interface Platform {
  stop: number;
  dir: Dir;
  /** Arrival rate per minute by destination: rate[bucket * n + d]. */
  rate: Float64Array;
  /** Arrival rate per minute, all destinations: total[bucket]. */
  total: Float64Array;
  /** Waiting riders by destination. Their sum is W. */
  q: Float64Array;
  W: number;
  /** Waiting riders in the order they came (FIFO from head). */
  parcels: Parcel[];
  head: number;
  /** Arrivals have been added to the queue up to this time. */
  matTime: number;
  waiting: Float32Array;
  /** Next minute of `waiting` to fill. */
  sampled: number;
  arrivals: number;
  boarded: number;
  leftBehind: number;
  turnedAway: number;
  gaveUp: number;
  waitTotal: number;
  waitMax: number;
  over10: number;
  maxWaiting: number;
  maxWaitingAt: number;
  calls: number;
}

/** A trip under way: who is aboard, by destination. */
interface Vehicle {
  load: Float64Array;
  onboard: number;
  result: TripResult;
}

export function simulate(input: SimInput): SimResult {
  const stops = input.stops;
  const n = stops.length;
  const capacity = Math.max(1, input.params.busCapacity);
  const factor = Math.max(0, input.params.demandFactor);
  const maxWait = Math.max(1, input.params.maxWaitMin);
  const bucketMin = Math.max(1, input.bucketMin);

  // ── the span of the day ──
  let firstMin = Infinity;
  let lastMin = -Infinity;
  for (const b of input.demand) {
    firstMin = Math.min(firstMin, b.startMin);
    lastMin = Math.max(lastMin, b.startMin + bucketMin);
  }
  for (const t of input.trips) {
    if (t.calls.length === 0) continue;
    firstMin = Math.min(firstMin, t.calls[0].timeMin);
    lastMin = Math.max(lastMin, t.calls[t.calls.length - 1].timeMin);
  }
  if (!Number.isFinite(firstMin)) { firstMin = 0; lastMin = 0; }
  firstMin = Math.max(0, Math.floor(firstMin));
  lastMin = Math.max(firstMin, Math.ceil(lastMin));
  const minutes = lastMin + 1;
  const buckets = Math.floor(lastMin / bucketMin) + 1;

  // ── platforms: one queue per stop and direction ──
  const platforms: Platform[] = [];
  for (let s = 0; s < n; s++) {
    for (const dir of DIRS) {
      platforms[stopDirIndex(s, dir)] = {
        stop: s, dir,
        rate: new Float64Array(buckets * n),
        total: new Float64Array(buckets),
        q: new Float64Array(n),
        W: 0,
        parcels: [], head: 0,
        matTime: 0,
        waiting: new Float32Array(minutes),
        sampled: 0,
        arrivals: 0, boarded: 0, leftBehind: 0, turnedAway: 0, gaveUp: 0,
        waitTotal: 0, waitMax: 0, over10: 0,
        maxWaiting: 0, maxWaitingAt: -1,
        calls: 0,
      };
    }
  }
  for (const b of input.demand) {
    const bi = Math.floor(b.startMin / bucketMin);
    if (bi < 0 || bi >= buckets) continue;
    for (let o = 0; o < n; o++) {
      const row = b.od[o];
      if (!row) continue;
      for (let d = 0; d < n; d++) {
        const riders = (row[d] ?? 0) * factor;
        if (!(riders > 0) || d === o) continue;
        const p = platforms[stopDirIndex(o, d > o ? "upward" : "downward")];
        const perMin = riders / bucketMin;
        p.rate[bi * n + d] += perMin;
        p.total[bi] += perMin;
        p.arrivals += riders;
      }
    }
  }

  /** Riders arriving at p during [a, b). */
  const arrivalsBetween = (p: Platform, a: number, b: number): number => {
    if (b <= a) return 0;
    let sum = 0;
    for (let bi = Math.floor(a / bucketMin); bi < buckets && bi * bucketMin < b; bi++) {
      const s = Math.max(a, bi * bucketMin);
      const e = Math.min(b, (bi + 1) * bucketMin);
      if (e > s) sum += p.total[bi] * (e - s);
    }
    return sum;
  };

  /** Take out of the queue everyone who has now waited longer than their patience. */
  const expire = (p: Platform, t: number) => {
    const cutoff = t - maxWait;
    const leave = (riders: number) => {
      if (riders <= EPS || p.W <= EPS) return;
      const share = Math.min(1, riders / p.W);
      for (let d = 0; d < n; d++) p.q[d] -= p.q[d] * share;
      p.W = Math.max(0, p.W - riders);
      p.gaveUp += riders;
    };
    while (p.head < p.parcels.length) {
      const parcel = p.parcels[p.head];
      if (parcel.from >= cutoff) break;
      if (parcel.to <= cutoff) {
        leave(parcel.riders);
        p.head++;
        continue;
      }
      // Only those who came before the cutoff have run out of patience.
      const frac = (cutoff - parcel.from) / (parcel.to - parcel.from);
      const leaving = parcel.riders * frac;
      leave(leaving);
      parcel.riders -= leaving;
      parcel.from = cutoff;
      break;
    }
  };

  /** Record the queue at every whole minute from the last sample up to t. */
  const sample = (p: Platform, t: number) => {
    const end = Math.min(t, minutes);
    for (let m = p.sampled; m < end; m++) {
      expire(p, m);
      // Arrivals since the last call are not in the queue yet; those still
      // within their patience are waiting all the same.
      p.waiting[m] = p.W + arrivalsBetween(p, Math.max(p.matTime, m - maxWait), m);
      p.sampled = m + 1;
    }
  };

  /** Add everyone who arrived between the last call and t to the queue. */
  const materialise = (p: Platform, t: number) => {
    if (t <= p.matTime) return;
    for (let bi = Math.floor(p.matTime / bucketMin); bi < buckets && bi * bucketMin < t; bi++) {
      const s = Math.max(p.matTime, bi * bucketMin);
      const e = Math.min(t, (bi + 1) * bucketMin);
      if (e <= s) continue;
      const riders = p.total[bi] * (e - s);
      if (riders <= EPS) continue;
      p.parcels.push({ from: s, to: e, riders, leftOnce: false });
      for (let d = 0; d < n; d++) {
        const r = p.rate[bi * n + d];
        if (r > 0) p.q[d] += r * (e - s);
      }
      p.W += riders;
    }
    p.matTime = t;
  };

  const waitHistogram: number[] = [];
  const addWait = (p: Platform, from: number, to: number, riders: number, t: number) => {
    if (riders <= EPS) return;
    const len = Math.max(0, to - from);
    const lo = Math.max(0, t - to);
    const hi = Math.max(lo, t - from);
    p.waitTotal += riders * (hi + lo) / 2;
    if (hi > p.waitMax) p.waitMax = hi;
    if (len <= EPS) {
      if (hi > LONG_WAIT_MIN) p.over10 += riders;
      const bin = Math.floor(hi);
      waitHistogram[bin] = (waitHistogram[bin] ?? 0) + riders;
      return;
    }
    p.over10 += riders * Math.min(1, Math.max(0, (hi - LONG_WAIT_MIN) / len));
    for (let k = Math.floor(lo); k < hi; k++) {
      const overlap = Math.min(k + 1, hi) - Math.max(k, lo);
      if (overlap > 0) waitHistogram[k] = (waitHistogram[k] ?? 0) + riders * overlap / len;
    }
  };

  /** Board `riders` from the head of the queue, oldest first, and account their waits. */
  const consume = (p: Platform, riders: number, t: number) => {
    let rem = riders;
    while (rem > EPS && p.head < p.parcels.length) {
      const parcel = p.parcels[p.head];
      if (parcel.riders <= rem + EPS) {
        addWait(p, parcel.from, parcel.to, parcel.riders, t);
        rem -= parcel.riders;
        p.head++;
      } else {
        const frac = rem / parcel.riders;
        const cut = parcel.from + (parcel.to - parcel.from) * frac;
        addWait(p, parcel.from, cut, rem, t);
        parcel.from = cut;
        parcel.riders -= rem;
        rem = 0;
      }
    }
    // Keep the head from walking off: drop consumed parcels now and then.
    if (p.head > 64 && p.head * 2 > p.parcels.length) {
      p.parcels.splice(0, p.head);
      p.head = 0;
    }
  };

  // ── trips as a single time-ordered list of calls ──
  interface CallRef { time: number; trip: SimTrip; index: number; order: number }
  const calls: CallRef[] = [];
  const results = new Map<string, Vehicle>();
  const tripResults: TripResult[] = [];
  input.trips.forEach((trip, order) => {
    if (trip.calls.length === 0) return;
    const result: TripResult = {
      trip,
      calls: [],
      linkLoads: new Array<number>(Math.max(0, trip.calls.length - 1)).fill(0),
      maxLoad: 0,
      maxLoadLink: -1,
      loadFactor: 0,
      riders: 0,
      leftBehind: 0,
    };
    tripResults.push(result);
    results.set(trip.id, { load: new Float64Array(n), onboard: 0, result });
    trip.calls.forEach((c, index) => calls.push({ time: c.timeMin, trip, index, order }));
  });
  // Same instant: the trip that set off first is served first, then the
  // earlier call of the same trip.
  calls.sort((a, b) => a.time - b.time || a.order - b.order || a.index - b.index);

  const events: LeftBehindEvent[] = [];

  for (const ref of calls) {
    const { trip, index } = ref;
    const t = ref.time;
    const call = trip.calls[index];
    const last = index === trip.calls.length - 1;
    const v = results.get(trip.id)!;
    const p = platforms[stopDirIndex(call.stop, trip.dir)];

    // Everyone who reached the stop before the bus, and has not given up, is
    // in the queue.
    sample(p, t);
    materialise(p, t);
    expire(p, t);
    p.calls++;

    let alighted = v.load[call.stop];
    v.load[call.stop] = 0;
    v.onboard -= alighted;
    if (last) {
      // Whoever is still aboard gets off at the end of the line.
      alighted += Math.max(0, v.onboard);
      v.load.fill(0);
      v.onboard = 0;
    }

    let boarded = 0;
    let leftBehind = 0;
    if (!last) {
      const W = p.W;
      if (W > p.maxWaiting) { p.maxWaiting = W; p.maxWaitingAt = t; }
      const room = Math.max(0, capacity - v.onboard);
      boarded = Math.min(W, room);
      leftBehind = Math.max(0, W - boarded);
      if (boarded > EPS) {
        const share = boarded / W;
        for (let d = 0; d < n; d++) {
          const b = p.q[d] * share;
          if (b <= 0) continue;
          p.q[d] -= b;
          v.load[d] += b;
        }
        v.onboard += boarded;
        p.W = Math.max(0, W - boarded);
        p.boarded += boarded;
        consume(p, boarded, t);
      }
      if (leftBehind > EPS) {
        p.turnedAway += leftBehind;
        v.result.leftBehind += leftBehind;
        // Whoever is still in the queue has now been passed by a full bus;
        // count each of them once, however many more pass them.
        for (let i = p.head; i < p.parcels.length; i++) {
          const parcel = p.parcels[i];
          if (parcel.leftOnce) continue;
          parcel.leftOnce = true;
          p.leftBehind += parcel.riders;
        }
        if (leftBehind >= EVENT_MIN_RIDERS) {
          events.push({ timeMin: t, stop: call.stop, dir: trip.dir, tripId: trip.id, riders: leftBehind });
        }
      }
    }
    // The minute of the call shows the queue once the bus has left.
    if (Number.isInteger(t) && t < minutes && p.sampled === t) {
      p.waiting[t] = p.W;
      p.sampled = t + 1;
    }

    const cr: TripCallResult = { stop: call.stop, timeMin: t, alighted, boarded, leftBehind, load: v.onboard };
    v.result.calls.push(cr);
    v.result.riders += boarded;
    if (!last) {
      v.result.linkLoads[index] = v.onboard;
      if (v.onboard > v.result.maxLoad) {
        v.result.maxLoad = v.onboard;
        v.result.maxLoadLink = index;
      }
    }
  }

  // ── after the last bus ──
  const stopDirs: StopDirResult[] = [];
  for (const p of platforms) {
    sample(p, minutes);
    materialise(p, lastMin);
    expire(p, lastMin);
    // Whoever is still at the stop when the day ends never got a bus either.
    const unserved = p.gaveUp + Math.max(0, p.W);
    stopDirs[stopDirIndex(p.stop, p.dir)] = {
      stop: p.stop,
      dir: p.dir,
      arrivals: p.arrivals,
      boarded: p.boarded,
      leftBehind: p.leftBehind,
      turnedAway: p.turnedAway,
      unserved,
      gaveUp: p.gaveUp,
      waitMeanMin: p.boarded > EPS ? p.waitTotal / p.boarded : 0,
      waitMaxMin: p.waitMax,
      waitOver10: p.over10,
      waiting: p.waiting,
      maxWaiting: p.maxWaiting,
      maxWaitingAt: p.maxWaitingAt,
      calls: p.calls,
    };
  }

  for (const r of tripResults) {
    r.loadFactor = r.maxLoad / capacity;
  }

  // ── vehicles ──
  const busesInService = new Float32Array(minutes);
  const spans = new Map<number, { start: number; end: number }>();
  let looseHours = 0;
  const loose: Array<{ start: number; end: number }> = [];
  for (const trip of input.trips) {
    if (trip.calls.length === 0) continue;
    const start = trip.calls[0].timeMin;
    const end = trip.calls[trip.calls.length - 1].timeMin;
    if (trip.block > 0) {
      const span = spans.get(trip.block);
      if (!span) spans.set(trip.block, { start, end });
      else { span.start = Math.min(span.start, start); span.end = Math.max(span.end, end); }
    } else {
      loose.push({ start, end });
      looseHours += Math.max(0, end - start) / 60;
    }
  }
  const mark = (span: { start: number; end: number }) => {
    for (let m = Math.max(0, Math.floor(span.start)); m < Math.min(minutes, Math.ceil(span.end)); m++) busesInService[m] += 1;
  };
  let busHours = looseHours;
  for (const span of spans.values()) { mark(span); busHours += Math.max(0, span.end - span.start) / 60; }
  const looseAtOnce = new Float32Array(minutes);
  for (const span of loose) {
    for (let m = Math.max(0, Math.floor(span.start)); m < Math.min(minutes, Math.ceil(span.end)); m++) looseAtOnce[m] += 1;
  }
  let looseMax = 0;
  for (let m = 0; m < minutes; m++) { busesInService[m] += looseAtOnce[m]; looseMax = Math.max(looseMax, looseAtOnce[m]); }
  const buses = spans.size + looseMax;

  // ── totals ──
  const totals: SimTotals = {
    arrivals: 0, boarded: 0, leftBehind: 0, turnedAway: 0, unserved: 0, gaveUp: 0,
    waitMeanMin: 0, waitMaxMin: 0, waitOver10: 0, waitOver10Share: 0,
    trips: tripResults.length, tripsFull: 0, tripsEmpty: 0, linksCrowdedShare: 0,
    buses, busHours, ridersPerBusHour: 0, utilisation: 0,
    firstMin, lastMin,
  };
  let waitTotal = 0;
  for (const p of platforms) {
    totals.arrivals += p.arrivals;
    totals.boarded += p.boarded;
    totals.leftBehind += p.leftBehind;
    totals.turnedAway += p.turnedAway;
    totals.waitOver10 += p.over10;
    waitTotal += p.waitTotal;
    totals.waitMaxMin = Math.max(totals.waitMaxMin, p.waitMax);
  }
  for (const sd of stopDirs) { totals.unserved += sd.unserved; totals.gaveUp += sd.gaveUp; }
  totals.waitMeanMin = totals.boarded > EPS ? waitTotal / totals.boarded : 0;
  totals.waitOver10Share = totals.boarded > EPS ? totals.waitOver10 / totals.boarded : 0;

  let links = 0;
  let crowded = 0;
  let riderLinks = 0;
  for (const r of tripResults) {
    if (r.leftBehind > EVENT_MIN_RIDERS) totals.tripsFull++;
    if (r.maxLoad < EMPTY_LOAD * capacity) totals.tripsEmpty++;
    for (const load of r.linkLoads) {
      links++;
      riderLinks += load;
      if (load >= CROWDED_LOAD * capacity - EPS) crowded++;
    }
  }
  totals.linksCrowdedShare = links > 0 ? crowded / links : 0;
  totals.utilisation = links > 0 ? riderLinks / (links * capacity) : 0;
  totals.ridersPerBusHour = busHours > EPS ? totals.boarded / busHours : 0;

  return {
    input,
    capacity,
    trips: tripResults,
    stopDirs,
    events,
    busesInService,
    waitHistogram: Array.from(waitHistogram, (v) => v ?? 0),
    totals,
  };
}

// ── reading a result ───────────────────────────────────────────────────────

/** Where a trip is at time t: between which calls, and how far along. Null when it is not running. */
export function positionAt(trip: SimTrip, t: number): { from: number; to: number; frac: number } | null {
  const calls = trip.calls;
  if (calls.length === 0 || t < calls[0].timeMin || t > calls[calls.length - 1].timeMin) return null;
  for (let i = 0; i < calls.length - 1; i++) {
    const a = calls[i].timeMin;
    const b = calls[i + 1].timeMin;
    if (t <= b) {
      const frac = b > a ? (t - a) / (b - a) : 1;
      return { from: i, to: i + 1, frac: Math.min(1, Math.max(0, frac)) };
    }
  }
  return { from: calls.length - 1, to: calls.length - 1, frac: 1 };
}

/** Riders aboard a trip at time t, from its link loads. */
export function loadAt(result: TripResult, t: number): number {
  const pos = positionAt(result.trip, t);
  if (!pos) return 0;
  if (pos.from === pos.to) return 0;
  return result.linkLoads[pos.from] ?? 0;
}

/** The simulated day as a span in whole minutes. */
export function daySpan(result: SimResult): [number, number] {
  return [result.totals.firstMin, result.totals.lastMin];
}

/** Demand-bucket input for a one-off run, from already-built pieces. */
export function buildInput(
  analysis: DemandAnalysis,
  rows: ScheduleImportRow[],
  params: SimInput["params"]
): SimInput {
  const base = demandFromAnalysis(analysis);
  return { ...base, trips: tripsFromRows(rows, analysis.stops), params };
}

/** Riders per day on the route, for a summary line. */
export function ridersPerDay(buckets: SimDemandBucket[], factor = 1): number {
  let sum = 0;
  for (const b of buckets) for (const row of b.od) for (const v of row) sum += v;
  return sum * factor;
}

